import "./historyTable.scss";
import { Button } from "antd";
import {
    DollarOutlined,
    EditOutlined,
    FileTextOutlined,
    MailOutlined,
    MobileOutlined,
    SendOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { EventInitiatorType, EventType, type PatientHistoryResult, RefundStatus } from "api/responses";
import { VyneTable } from "@vyne-shared/components/vyneTable/VyneTable";
import { dollarsFromBasisPoints, stringFromDollarsShowPositiveSymbol } from "@vyne-shared/utils/currency";
import { formatDateNumerically } from "@vyne-shared/utils/date";
import { partialRefundExpandable } from "./partialRefundExpandable";
import { showRefundModal } from "components/PaymentsLanding/RefundModal/showRefundModal";
import vyneLogo from "assets/vyne_dental_logo_sm.png";
import type { ColumnType } from "antd/es/table";
import type { HistoryTableProps } from "./historyTable.props";

/** Get CSS class for transaction amount. */
const getTransactionAmountClass = (amount: number) => (amount < 0 ? "negamount" : "posamount");

/** Get CSS class for transaction events that are refunded. */
function getRefundClass(event: PatientHistoryResult) {
    if (event.refundStatus === RefundStatus.Refunded) {
        return "refunded";
    }
    if (event.refundStatus === RefundStatus.RefundedPartial) {
        return "partially-refunded";
    }
    return "";
}

function getEventTypeDisplay(event: PatientHistoryResult) {
    switch (event.type) {
        case EventType.Transaction: {
            switch (event.txSubType) {
                case "Payment":
                    return { icon: <DollarOutlined />, label: "Payment" };
                case "Request":
                    return { icon: <SendOutlined />, label: "Payment Request" };
                case "AutoAdjust":
                    return { icon: <ThunderboltOutlined />, label: "Automatic Adjustment" };
                case "ManAdjust":
                    return { icon: <EditOutlined />, label: "Manual Adjustment" };
                default:
                    // shouldn't ever hit this
                    return { icon: <DollarOutlined />, label: "Generic Transaction" };
            }
        }

        case EventType.Communication: {
            switch (event.commChannel) {
                case "Email":
                    return { icon: <MailOutlined />, label: "Email" };
                case "SMS":
                    return { icon: <MobileOutlined />, label: "SMS Text" };
                case "Statement":
                    return { icon: <FileTextOutlined />, label: "Paper Statement" };
            }
        }
    }
}

/** Display specific event type with helpful icon. */
const EventTypeDisplay = ({ event }: { readonly event: PatientHistoryResult }) => {
    const display = getEventTypeDisplay(event);
    return display ? (
        <>
            {display.icon} {display.label}
        </>
    ) : null;
};

/** Display who initiated the event (including user name if possible) with a helpful icon */
const EventInitiatorDisplay = ({ event }: { readonly event: PatientHistoryResult }) =>
    event.initiator === EventInitiatorType.BillingAdmin ? (
        <>
            <TeamOutlined title="Billing Administrator" /> {event.initiatorName ?? "Billing Administrator"}
        </>
    ) : event.initiator === EventInitiatorType.Patient ? (
        <>
            <UserOutlined title="Patient/Responsible Party" /> Patient
        </>
    ) : (
        <>
            <img src={vyneLogo} alt="Vyne Dental logo" title="Vyne System User" /> Vyne Payments
        </>
    );

/** Display what the communication was about. */
const CommunicationSubTypeDisplay = ({ event }: { readonly event: PatientHistoryResult }) =>
    event.subType === "UpdatedBalance" ? (
        <>Payment Request</>
    ) : event.subType === "ResendBalance" ? (
        <>Resend Request</>
    ) : event.subType === "ConfirmPayment" ? (
        <>Receipt</>
    ) : null;

interface RowProps {
    readonly event: PatientHistoryResult;
    readonly patientId: number;
    readonly hideModal?: () => void;
}

/** Helper to display refund status or action button. */
const RefundDisplay = ({ event, patientId, hideModal }: RowProps) => {
    if (event.refundStatus === RefundStatus.Possible || event.refundStatus === RefundStatus.RefundedPartial) {
        return (
            <Button
                size="small"
                type="link"
                onClick={() => {
                    hideModal?.();
                    void showRefundModal(patientId, event.eventId, true);
                }}
            >
                Refund
            </Button>
        );
    }

    return null;
};

const standardColumns: ColumnType<PatientHistoryResult>[] = [
    {
        title: "Date",
        dataIndex: "eventDate",
        defaultSortOrder: "descend",
        render: (dateNum: number) => {
            const eventDate = new Date(dateNum);
            return (
                <time dateTime={eventDate.toISOString()} title={eventDate.toLocaleString()}>
                    {formatDateNumerically(eventDate)}
                </time>
            );
        },
        sorter: (a, b) => a.eventDate - b.eventDate,
    },
    {
        title: "Type",
        dataIndex: "type",
        render: (_, eventRecord) => <EventTypeDisplay event={eventRecord} />,
    },
    {
        title: "User",
        dataIndex: "initiator",
        render: (_, eventRecord) => <EventInitiatorDisplay event={eventRecord} />,
    },
    {
        title: "Amount",
        key: "amount",
        render: (_, eventRecord) => {
            if (eventRecord.type === EventType.Transaction) {
                const amount = eventRecord.amount ?? 0;
                return (
                    <span className={getTransactionAmountClass(amount)}>
                        {stringFromDollarsShowPositiveSymbol(dollarsFromBasisPoints(amount))}
                    </span>
                );
            } else {
                return <CommunicationSubTypeDisplay event={eventRecord} />;
            }
        },
        sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
    },
];

/** Generate columns for event history table */
function generateHistoryColumns(
    patientId: number,
    hideModal?: () => void,
    hideActions?: boolean,
    hideSorting?: boolean,
) {
    const columns = standardColumns.map<ColumnType<PatientHistoryResult>>((col) => ({
        ...col,
        sorter: hideSorting ? undefined : col.sorter,
    }));

    if (!hideActions) {
        columns.push({
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, eventRecord) => (
                <div className="user-actions">
                    <RefundDisplay event={eventRecord} patientId={patientId} hideModal={hideModal} />
                </div>
            ),
        });
    }

    return columns;
}

/** Display a table of patient history events (transactions and communications). */
export function HistoryTable<T extends object>({
    patientId,
    events,
    hideModal,
    loading,
    hideActions,
    hideSorting,
    erroring,
    name,
}: Readonly<HistoryTableProps<T>>) {
    return (
        <VyneTable
            name={name}
            erroring={erroring}
            loading={loading}
            expandable={partialRefundExpandable}
            rowKey="eventId"
            dataSource={events}
            columns={generateHistoryColumns(patientId, hideModal, hideActions, hideSorting)}
            className="history-table"
            data-testid="history-table"
            pagination={{
                defaultPageSize: 10,
                pageSizeOptions: [10, 50, 100, 500],
            }}
            rowClassName={getRefundClass}
            locale={{ emptyText: "No events found." }}
        />
    );
}
